import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType  } from '@ngrx/effects';
import { catchError, map, exhaustMap, withLatestFrom, switchMap  } from 'rxjs/operators';
import {  of } from 'rxjs';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { MeasuresService } from '@services/measures.service';
import { MeasuresActions } from '@store/actions/measures/measures.actions';
import { Store } from '@ngrx/store';
import * as fromMeasures from '../../selectors/measures/measures.selectors'
import { IComponent, ICreateCriteriaGroupRequest, ICriteria, ICriteriaReorderRequest, IMeasure, IRule, IRuleReorderRequest, IUpdateMeasureRequest } from '@models/measures.model';


@Injectable()
export class MeasuresEffects { 
  private readonly store = inject(Store);

  getMeasuresTableData$  = createEffect(() =>{
    return this.actions$.pipe(
      ofType(MeasuresActions.measuresComponentLoaded),
      exhaustMap(() => {
          return this.measuresService.getMeasuresTableData({}).pipe( 
            map(data => MeasuresActions.getMeasuresSuccess({ measuresData: data, totalItems: data.length })),
            catchError(error => of(MeasuresActions.getMeasuresFailure({ error }))
          )
        );
      })
    )
  });
  
  getMeasuresCriteriaGroups$  = createEffect(() =>{
    return this.actions$.pipe(
      ofType(MeasuresActions.measuresComponentLoaded),
      exhaustMap(() => {
          return this.measuresService.getMeasureCriteriaGroups().pipe( 
            map(data =>  MeasuresActions.loadCriteriaGroupTypes({ criteriaGroupTypes: data }) ),
            catchError(error => of(MeasuresActions.loadCriteriaGroupTypesFailure({ error }))
          )
        );
      })
    )
  });

  getMeasuresAggregationDropdown$  = createEffect(() =>{
    return this.actions$.pipe(
      ofType(MeasuresActions.measuresComponentLoaded),
      exhaustMap(() => {
          return this.measuresService.getMeasureAggregationTypes().pipe( 
            map(data =>  MeasuresActions.loadMeasureAggregationTypes({ measureAggregationTypes: data }) ),
            catchError(error => of(MeasuresActions.loadMeasureAggregationFailure({ error }))
          )
        );
      })
    )
  });

  getMeasuresTypesDropdown$  = createEffect(() =>{
    return this.actions$.pipe(
      ofType(MeasuresActions.measuresComponentLoaded),
      switchMap(() => {
          return this.measuresService.getMeasureTypes().pipe( 
            map(data => MeasuresActions.loadMeasureTypes({ measureTypes: data })),
            catchError(error => of(MeasuresActions.loadMeasureTypesFailure({ error }))
          )
        );
      })
    )
  });
  
  getMeasureDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeasuresActions.setMeasureToEdit),
      switchMap((action) => {
        return this.measuresService.getMeasureDetails(action.measure.id).pipe( 
          map(data => MeasuresActions.setMeasureDetails({ measure: action.measure, details : data.criterias })),
          catchError(error => of(MeasuresActions.getMeasuresFailure({ error }))
        )
      )
    })
    )
  });

  addNewCriteria$ = createEffect(() => {
    let request: ICreateCriteriaGroupRequest = {
      measureID: 0,
      createUserId: 0
    }

    return this.actions$.pipe(
      ofType(MeasuresActions.clickAddNewCriteria),
      withLatestFrom(this.store.select<IMeasure|null>(fromMeasures.selectCurrentMeasureEdit)),
      map(([action, measure]) => {
        request.measureID = measure!.id;
      }),
      switchMap((action) => {
        return this.measuresService.createCriteriaGroup(request).pipe( 
          map(data => MeasuresActions.addNewCriteriaSuccess({ criteria : data})),
          catchError(error => of(MeasuresActions.addMeasureComponentFailure({ error }))
          ))
        })
    )
  });

  addNewComponent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeasuresActions.addMeasureComponent),
      switchMap((action) => {
        return this.measuresService.createMeasureComponent(action.request).pipe( 
          map(result => MeasuresActions.addMeasureComponentSuccess({ component: result })),
          catchError(error => of(MeasuresActions.addMeasureComponentFailure({ error }))
          ))
        })
    )
  });

  updateComponentId$ = createEffect(() => {
    
    return this.actions$.pipe(
      ofType(MeasuresActions.addMeasureComponentSuccess),
      withLatestFrom(this.store.select<ICriteria[]>(fromMeasures.selectCriteriaList)),
      map(([actions , criterias]) =>{

        let newCriteria = criterias.map(criteria => ({
          ...criteria,
          components: [...criteria.components]
        }));

        const component: IComponent = {
          id: actions.component.id,
          name: actions.component.name,
          sortOrder: actions.component.sortOrder,
          criteriaGroupTypeID: actions.component.criteriaGroupTypeID,
          ruleGroupId: actions.component.ruleGroupID,
          rules: []
        }
    
        newCriteria.find( c => c.id == actions.component.populationCriteriaGroupID)?.components.push(component)
        return MeasuresActions.updateCriteriaSuccess({ criterias : newCriteria})
      })      
    )
  });

  reOrderCriteria$ = createEffect(() =>{
    let newCriteriaList: ICriteria[];
    let request: ICriteriaReorderRequest = {
      measureId: 0,
      modifyUserId: 0,
      populationCriteriaGroups: []
    };

    return this.actions$.pipe(
      ofType(MeasuresActions.reorderCriteriaList),
      withLatestFrom(this.store.select<ICriteria[]>(fromMeasures.selectCriteriaList),
        this.store.select<IMeasure|null>(fromMeasures.selectCurrentMeasureEdit)
      ),
      map(([action, criteriaList, measure]) => {
        newCriteriaList = [...criteriaList];
        
        request.measureId = measure!.id;
        moveItemInArray(newCriteriaList, action.previous, action.current);

        newCriteriaList = newCriteriaList.map((criteria, index) => ({
          ...criteria,
          sortOrder: index + 1
        }));

        request.populationCriteriaGroups = newCriteriaList.map((criteria) => ({
          id: criteria.id,
          sortOrder: criteria.sortOrder,
        }));
      }),
      switchMap((action) => {
        return this.measuresService.reorderCriteriaGroup(request).pipe( 
          map(data => MeasuresActions.reorderCriteriaListSuccess({ criterias : newCriteriaList})),
          catchError(error => of(MeasuresActions.reorderCriteriaListFailure({ error }))
          ))
        })
    )
  });

  reOrderRules$ = createEffect(() =>{
    let newCriteriaList: ICriteria[];
    let request: IRuleReorderRequest = {
      ruleGroupId: 0,
      modifyUserId: 0,
      rules: []
    };

    return this.actions$.pipe(
      ofType(MeasuresActions.reorderRuleList),
      withLatestFrom(this.store.select<ICriteria[]>(fromMeasures.selectCriteriaList)
      ),
      map(([action, criteriaList]) => {
        newCriteriaList = criteriaList.map(criteria => ({
          ...criteria,
          components: criteria.components.map(component => ({
            ...component,
            rules: [...component.rules]
          }))
        }));
        
        request.ruleGroupId = newCriteriaList.find( c => c.id == action.criteriaId)!
          .components.find( comp => comp.id == action.componentId)!.ruleGroupId

        moveItemInArray(newCriteriaList.find( c => c.id == action.criteriaId)!
          .components.find( comp => comp.id == action.componentId)!.rules, 
          action.previous, 
          action.current);

        request.rules = newCriteriaList.find( c => c.id == action.criteriaId)!
              .components.find( comp => comp.id == action.componentId)!.rules
              .map((rule, index) => ({
                id: rule.id,
                sortOrder: index + 1,
        }));        

      }),
      switchMap((action) => {
        return this.measuresService.reorderRuleGroup(request).pipe( 
          map(data => MeasuresActions.reorderRuleListSuccess({ criterias : newCriteriaList})),
          catchError(error => of(MeasuresActions.reorderRuleListFailure({ error }))
          ))
        })
    )
  });

  updateMeasureData$ = createEffect(() => {

    return this.actions$.pipe(
      ofType(MeasuresActions.clickSaveMeasure),
      withLatestFrom(this.store.select<IMeasure>(fromMeasures.selectCurrentMeasureEdit)),
      map(([action, measure]) => {
        return measure;
      }),
      switchMap((measure) => {

        const request:IUpdateMeasureRequest = {
          id: measure.id,
          researchNetworkID: measure.researchNetworkID,
          registryID: measure.registryID,
          title: measure.title,
          description: measure.description,
          eMeasure_Measure_identifier: measure.eMeasure_Measure_identifier,
          nqF_eMeasure_identifier: measure.nqF_eMeasure_identifier,
          nqF_identifier: measure.nqF_identifier,
          qpP_identifier: measure.qpP_identifier,
          measureTypeID: measure.measureTypeID,
          highPriority: measure.highPriority,
          measureAggregationTypeID: measure.measureAggregationTypeID,
          updateUserID: 0
        }

        return this.measuresService.updateMeasureData(request).pipe( 
          map(data => MeasuresActions.clickSaveMeasureSuccess()),
          catchError(error => of(MeasuresActions.clickSaveMeasureFailure({ error }))
          ))
        })
    )
  });

  addNewRule$ = createEffect(() =>{
    return this.actions$.pipe(
      ofType(MeasuresActions.addNewRule),
      withLatestFrom(this.store.select<ICriteria[]>(fromMeasures.selectCriteriaList)),
      map(([actions , criterias]) =>{

        const newRule: IRule ={
          id: actions.ruleId,
          sortOrder: actions.sortOrder
        }
        
        let newCriteria = criterias.map(criteria => ({
          ...criteria,
          components: criteria.components.map(component => ({
            ...component,
            rules: [...component.rules]
          }))
        }));

        newCriteria.find( c => c.id == actions.criteriaId)?.components
          .find( co => co.id == actions.componentId)?.rules.push(newRule)

        return MeasuresActions.addNewRuleSuccess({ details : newCriteria})
      })      
    )
  });

  constructor(private actions$: Actions, private measuresService: MeasuresService) {}
}
